import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { classService } from "../services/api/classes";
import { ClassFormData } from "../types/classes";
import toast from "react-hot-toast";

export const useClasses = (instituteId: number) => {
    const queryClient = useQueryClient();

    const queryKeys = {
        classes: (instituteId: number, academicYearId?: number) => 
            academicYearId ? ['classes', instituteId, academicYearId] : ['classes', instituteId],
        classById: (instituteId: number, id: number) => ['class', instituteId, id], // Single class by ID
    };

    const classes = useQuery({
        queryKey: queryKeys.classes(instituteId),
        queryFn: () => classService.getAll(),
        staleTime: 5 * 60 * 1000,
    });

    const useClassesByYear = (academicYearId: number) => useQuery({
        queryKey: queryKeys.classes(instituteId, academicYearId),
        queryFn: () => classService.getAll(academicYearId),
        enabled: !!instituteId && !!academicYearId,
        staleTime: 5 * 60 * 1000,
    });

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const getClass = (id: number) => useQuery({
        queryKey: queryKeys.classById(instituteId, id),
        queryFn: () => classService.getById(id, instituteId),
        enabled: !!id,
        staleTime: 5 * 60 * 1000,
    });

    const addClass = useMutation({
        mutationFn: (data: ClassFormData) => classService.create(data, instituteId),
        onSuccess: () => {
            toast.success("Class Added Successfully");
            queryClient.invalidateQueries({ queryKey: queryKeys.classes(instituteId) });
        },
        onError: (error) =>{
            console.error("Error adding class:", error);
        }
    });

    const updateClass = useMutation({
        mutationFn: ({ id, data }: { id: number; data: ClassFormData }) =>
            classService.update(id, data, instituteId),
        onSuccess: (_,variables) =>{
            toast.success("Class Updated Successfully");
            queryClient.invalidateQueries({ queryKey: queryKeys.classes(instituteId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.classById(instituteId,variables.id) });
        },
        onError: (error) => {
            console.error("Error updating class:", error);
        } 
    });

    const deleteClass = useMutation({
        mutationFn: (id: number) => classService.delete(id, instituteId),
        onSuccess: (_,id) => {
            toast.success("Class deleted Successfully");
            queryClient.invalidateQueries({ queryKey: queryKeys.classes(instituteId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.classById(instituteId,id) });
        },
        onError: (error) => {
            console.error("Error deleting class:", error);
        }
    });
    const reorderClasses = useMutation({
        mutationFn: ({ instituteId, orders }: { instituteId: number; orders: { classId: number; displayOrder: number }[] }) =>
            classService.reorder(instituteId, orders),
        onError: (error) => {
            console.error("Error reordering classes:", error);
        },
    });

    const invalidateClasses = () => {
        return queryClient.invalidateQueries({ queryKey: queryKeys.classes(instituteId) });
    };
    return {
        classes: classes.data ?? [],
        isLoading: classes.isLoading,
        error: classes.error,
        isFetched: classes.isFetched,
        getClass,
        useClassesByYear,
        addClass,
        updateClass,
        deleteClass,
        invalidateClasses,
        reorderClasses,
    };
};